import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import countriesAndTimezones from 'countries-and-timezones';
import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';
import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import CheckboxField from 'components/fields/CheckboxField';
import AutosuggestField from 'components/fields/AutosuggestField';
import FormError from 'components/common/FormError';
import UploadField from 'components/fields/UploadField';
import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';

import { createBusiness, updateBusiness } from 'redux/modules/business';
import { updateFacility } from 'redux/modules/facilities';
import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';
import imageFileTypes from 'config/imageFileTypes';

const usaStates = new UsaStates();
const usaTimeZones = countriesAndTimezones.getTimezonesForCountry('US')
  .map(item => item.name);

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridGutter: {
    marginTop: 15,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  button: {
    marginLeft: 20,
  },
  form: {
    // width: 510,
    margin: '10px auto',
  },
  typography: {
    paddingBottom: 2,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
});

function onSubmit(values, dispatch, props) {
  if (props.pristine) {
    return values;
  }
  if (props.initialValues.ID) {
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateBusiness({
        values: {
          ID: props.initialValues.ID,
          ...diffValues.Image ? { Image: values.Image } : {},
          ...diffValues.Image === null ? { Image: null } : {},
          ...diffValues['Company Info'] ? { 'Company Info': {
            ID: values['Company Info'].ID,
            ...diffValues['Company Info'],
          } } : {},
          ...diffValues['Company Contact'] ? { 'Company Contact': {
            ID: values['Company Contact'].ID,
            ...diffValues['Company Contact'],
            ...diffValues['Company Contact']['Company Website'] === '' ? {
              'Company Website': null,
            } : {},
          } } : {},
          ...diffValues['Company Address'] ? { 'Company Address': {
            ...values['Company Address'].ID ? {
              ID: values['Company Address'].ID,
            } : {
              'Company ID': props.initialValues.ID,
            },
            ...diffValues['Company Address'],
            'Observes Daylight Saving': values['Company Address']['Observes Daylight Saving'] || false,
          } } : {},
          ...diffValues['Company Identify'] ? { 'Company Identify': {
            ...values['Company Identify'].ID ? {
              ID: values['Company Identify'].ID,
            } : {
              'Company ID': props.initialValues.ID,
            },
            ...diffValues['Company Identify'],
            ...diffValues['Company Identify']['Trading Partner ID'] === '' ? {
              'Trading Partner ID': null,
            } : {},
            ...diffValues['Company Identify'].MEDICAID === '' ? {
              MEDICAID: null,
            } : {},
            ...diffValues['Company Identify'].MEDICARE === '' ? {
              MEDICARE: null,
            } : {},
            ...diffValues['Company Identify']['Primary Taxonomy'] === '' ? {
              'Primary Taxonomy': null,
            } : {},
          } } : {},
        },
        resolve,
        reject,
      }));
    }).then(
      res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  return new Promise((resolve, reject) => {
    dispatch(createBusiness({
      values: {
        ...values,
        ...!values['Observes Daylight Saving'] ? {
          'Observes Daylight Saving': false,
        } : {},
      },
      resolve,
      reject,
    }));
  }).then(
    res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function onSubmitFail(errors) {
  const errorNames = Object.keys(errors);
  if (errorNames.length > 0) {
    const selectorString = errorNames.map(item => `[name="${item}"]`).join(', ');
    const firstErrorEl = document.querySelector(selectorString);
    if (firstErrorEl) {
      const firstErrorElOffsetTop = firstErrorEl.getBoundingClientRect().top;
      const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
      const scrollOffset = 50;
      if ((window.innerHeight - scrollOffset) < firstErrorElOffsetTop) {
        const pageHeaderHeight = 64;
        window.scrollTo(0, (firstErrorElOffsetTop
          + windowScrollTop) - pageHeaderHeight - scrollOffset);
        firstErrorEl.focus();
      }
    }
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Time Zone'] && usaTimeZones.indexOf(values['Time Zone']) === -1) {
    errors['Time Zone'] = 'Please, select Time Zone from list';
  }

  return errors;
};

class BusinessGeneralForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onResetCancel = this.onResetCancel.bind(this);
    this.onResetSubmit = this.onResetSubmit.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onResetCancel() {
    this.setState({ anchorEl: null });
  }

  onResetSubmit() {
    this.setState({ anchorEl: null });
    this.props.reset();
  }

  render () {
    const {
      submit,
      reset,
      error,
      handleSubmit,
      submitting,
      change,
      disabled,
      variant,
      history,
      classes,
      width,
    } = this.props;
    const { anchorEl } = this.state;
    return (
      <Section>
        <SectionHeader
          fixed
          {...variant === 'default' ? {
            top: 110,
          } : {}}
        >
          <SectionTitle>
            {variant === 'payment' ? 'Step 2: Confirm Business Information' : 'Company Settings'}
          </SectionTitle>
          <div>
            {variant === 'default' ? (
              <Fragment>
                <Button
                  className={classes.button}
                  variant="raised"
                  onClick={this.onMenuButtonClick}
                  disabled={disabled || submitting}
                >
                  Reset
                </Button>
                <Menu
                  id="reset-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={this.onMenuClose}
                  list={false}
                  maxHeight={130}
                >
                  <DeleteMenu
                    text="All changes will be reset, are you sure?"
                    onCancel={this.onResetCancel}
                    onSubmit={this.onResetSubmit}
                    submitButtonText="Submit"
                    submitButtonColor="primary"
                  />
                </Menu>
              </Fragment>
            ) : (
              <Button
                className={classes.button}
                variant="raised"
                onClick={() => {
                  history.goBack();
                }}
              >
                Back
              </Button>
            )}
            <Button
              className={classes.button}
              variant="raised"
              color="primary"
              disabled={disabled || submitting}
              onClick={submit}
            >
              {variant === 'payment' ? 'Next' : 'Save'}
            </Button>
          </div>
        </SectionHeader>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {error &&
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid
                item
                xs={12}
              >
                <FormError>
                  {error}
                </FormError>
              </Grid>
            </Grid>
          }
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid item xs={12} md={7}>
              <SectionBody>
                <Grid
                  className={classNames(classes.grid, classes.gridGutter)}
                  container
                  spacing={24}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      Contact Information
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Company Name"
                      name="Company Info.Company Name"
                      label="Company Name *"
                      component={TextField}
                      validate={[validate.required]}
                      disabled={disabled}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Company Website"
                      name="Company Contact.Company Website"
                      label="Company Website"
                      component={TextField}
                      disabled={disabled}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Signer First Name"
                      name="Company Info.Signer First Name"
                      label="Signer First Name *"
                      component={TextField}
                      validate={[validate.required]}
                      disabled={disabled}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Signer Last Name"
                      name="Company Info.Signer Last Name"
                      label="Signer Last Name *"
                      component={TextField}
                      validate={[validate.required]}
                      disabled={disabled}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Phone Number"
                      name="Company Contact.Phone Number"
                      label="Phone Number *"
                      component={TextField}
                      validate={[validate.required, validate.phone]}
                      format={value => (value ? value.replace('+1 ', '') : '')}
                      normalize={normalize.phone}
                      maxLength="14"
                      startAdornment={
                        <InputAdornment position="start">
                          <TextFieldAdornmentText>
                            +1
                          </TextFieldAdornmentText>
                        </InputAdornment>
                      }
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Fax"
                      name="Company Contact.Fax"
                      label="Fax *"
                      component={TextField}
                      validate={[validate.required, validate.phone]}
                      format={value => (value ? value.replace('+1 ', '') : '')}
                      normalize={normalize.phone}
                      maxLength="14"
                      startAdornment={
                        <InputAdornment position="start">
                          <TextFieldAdornmentText>
                            +1
                          </TextFieldAdornmentText>
                        </InputAdornment>
                      }
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Company Email"
                      name="Company Contact.Company Email"
                      label="Company Email *"
                      component={TextField}
                      validate={[validate.required, validate.email]}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  alignItems="flex-end"
                  spacing={24}
                >
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Time Zone"
                      name="Company Address.Time Zone"
                      label="Time Zone *"
                      component={AutosuggestField}
                      validate={[validate.required]}
                      placeholder="-Select Title-"
                      fullWidth
                      renderSuggestionContent={suggestion => suggestion}
                      onSuggestionSelected={suggestion => suggestion}
                      fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                        if (offset === 0) {
                          if (value.length > 0) {
                            resolve(usaTimeZones
                              .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                          } else {
                            resolve(usaTimeZones);
                          }
                        } else {
                          resolve([]);
                        }
                      }))}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      id="Observes daylight saving"
                      name="Company Address.Observes Daylight Saving"
                      label="Observes daylight saving"
                      component={CheckboxField}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </SectionBody>
            </Grid>
            <Grid item xs={12} md={5}>
              <SectionBody fullHeight>
                <Grid
                  className={classNames(classes.grid, classes.gridGutter)}
                  container
                  spacing={24}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      Company Logo
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid
                    item
                    xs={10}
                  >
                    <Field
                      id="Image"
                      name="Image"
                      component={UploadField}
                      variant="image"
                      maxSize={1000000}
                      accept={imageFileTypes}
                      label={(
                        <Fragment>
                          Drag file here <br />
                          JPG, PNG or GIF <br />
                          No larger than 1 MB <br />
                        </Fragment>
                      )}
                      fullWidth
                      showClearButton
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </SectionBody>
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid item xs={12} md={6}>
              <SectionBody>
                <Grid
                  className={classNames(classes.grid, classes.gridGutter)}
                  container
                  spacing={24}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      Identifiers
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={4}>
                    <Field
                      id="Practice NPI"
                      name="Company Identify.Practice NPI"
                      label="Practice NPI *"
                      component={TextField}
                      validate={[validate.required, validate.minLength10]}
                      maxLength="10"
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      id="EIN"
                      name="Company Identify.EIN"
                      label="EIN *"
                      component={TextField}
                      validate={[validate.required]}
                      maxLength="9"
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      id="Trading Partner ID"
                      name="Company Identify.Trading Partner ID"
                      label="Trading Partner ID"
                      component={TextField}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={12} md={4}>
                    <Field
                      id="MEDICARE"
                      name="Company Identify.MEDICARE"
                      label="MEDICARE"
                      component={TextField}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      id="MEDICAID"
                      name="Company Identify.MEDICAID"
                      label="MEDICAID"
                      component={TextField}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      id="Primary Taxonomy"
                      name="Company Identify.Primary Taxonomy"
                      label="Primary Taxonomy"
                      component={TextField}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </SectionBody>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionBody>
                <Grid
                  className={classNames(classes.grid, classes.gridGutter)}
                  container
                  spacing={24}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      Practice Address
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={9} md={8}>
                    <Field
                      id="Street"
                      name="Company Address.Street"
                      label="Street *"
                      component={AutosuggestField}
                      validate={[validate.required]}
                      placeholder="Lookup Address"
                      fullWidth
                      renderSuggestionContent={(suggestion) => suggestion.address.label}
                      onSuggestionSelected={(suggestion) => {
                        if (suggestion.address.city) {
                          change('Company Address.City', suggestion.address.city);
                        }
                        if (suggestion.address.postalCode) {
                          change('Company Address.Zip Code', suggestion.address.postalCode);
                        }
                        if (suggestion.address.stateCode) {
                          change('Company Address.State', suggestion.address.stateCode);
                        }
                        if (suggestion.address.street) {
                          if (suggestion.address.houseNumber) {
                            return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
                          }
                          return suggestion.address.street;
                        }
                        return suggestion.address.label;
                      }}
                      fetchSuggestions={({ value }) => (
                        value.length > 0
                          ? requestFetchPlacesAutocomplete({
                            q: value,
                            in: 'countryCode:USA',
                            limit: 5,
                          }).then(res => res.items,
                            err => err)
                          : new Promise(resolve => resolve([]))
                      )}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={3} md={3}>
                    <Field
                      id="Suite"
                      name="Company Address.Suite"
                      label={width === 'xs' || width === 'sm' ? 'Suite' : 'Suite (Optional)'}
                      component={TextField}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid item xs={6} md={5}>
                    <Field
                      id="City"
                      name="Company Address.City"
                      label="City *"
                      component={TextField}
                      validate={[validate.required]}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={3} md={3}>
                    <Field
                      id="State"
                      name="Company Address.State"
                      label="State *"
                      component={AutosuggestField}
                      normalize={normalize.uppercase}
                      validate={[validate.required]}
                      fullWidth
                      renderSuggestionContent={suggestion => suggestion.abbreviation}
                      onSuggestionSelected={suggestion => suggestion.abbreviation}
                      fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                        if (offset === 0) {
                          if (value.length > 0) {
                            resolve(usaStates.states.filter(item =>
                              item.abbreviation.indexOf(value) === 0));
                          } else {
                            resolve(usaStates.states);
                          }
                        } else {
                          resolve([]);
                        }
                      }))}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={3} md={3}>
                    <Field
                      id="Zip Code"
                      name="Company Address.Zip Code"
                      label="Zip Code *"
                      component={TextField}
                      validate={[validate.required]}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </SectionBody>
            </Grid>
          </Grid>
        </form>  
      </Section>
    );
  }
}

BusinessGeneralForm.propTypes = {
  variant: PropTypes.oneOf(['default', 'payment']),
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }),
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

BusinessGeneralForm.defaultProps = {
  error: null,
  disabled: false,
  variant: 'default',
  history: null,
};

export default reduxForm({
  form: 'settingsBusinessGeneral',
  onSubmit,
  onSubmitFail,
  validate: validateForm,
  enableReinitialize: true,
})(withStyles(styles)(withWidth()(BusinessGeneralForm)));



// WEBPACK FOOTER //
// ./src/components/settings/BusinessGeneralForm/BusinessGeneralForm.js